import { normaliserNumeroJoignable } from './telephone.js';

/**
 * Numéro WhatsApp d'un candidat et liens `wa.me`
 * (docs/FORMULAIRE_PUBLIC.md § 5 bis et § 5 ter).
 *
 * Ce numéro n'est **jamais** une clé de rapprochement : seuls le téléphone
 * principal normalisé et l'e-mail le sont. Une correspondance sur un WhatsApp
 * se signale sur la fiche, elle ne fusionne rien.
 *
 * `normaliserTelephone` rend `null` pour tout ce qui n'est pas un numéro
 * tunisien à 8 chiffres : un candidat joignable sur un numéro étranger ne
 * passerait pas. D'où une normalisation distincte (arbitrage du 2026-09-20),
 * qui garde le numéro international tel qu'il a été saisi, une fois nettoyé.
 *
 * L'application n'envoie **rien** : elle fabrique un lien que l'utilisateur
 * ouvre dans son propre WhatsApp, où il écrit et envoie lui-même.
 */

/**
 * `+216 22 123 456`, `22123456`, `0033 6 12 34 56 78`, `+33612345678` →
 * `+21622123456`, `+33612345678`. Rend `null` si rien d'exploitable.
 *
 * C'est `normaliserNumeroJoignable` (`telephone.ts`), sous le nom du seul
 * usage qu'il avait en naissant : un numéro sur lequel on peut joindre
 * quelqu'un, tunisien en forme canonique ou étranger seulement nettoyé. Le
 * numéro ajouté à la main sur une fiche emprunte la même règle.
 */
export function normaliserWhatsapp(brut: string | null | undefined): string | null {
  return normaliserNumeroJoignable(brut);
}

/**
 * Deux numéros désignent-ils le même abonné ? Sert à décider si la case
 * « c'est le même numéro que mon téléphone » doit être cochée d'office, jamais
 * à rapprocher deux personnes.
 */
export function memeNumero(a: string | null | undefined, b: string | null | undefined): boolean {
  const gauche = normaliserWhatsapp(a);
  const droite = normaliserWhatsapp(b);
  return gauche !== null && gauche === droite;
}

/**
 * Numéro WhatsApp effectif d'une personne : celui qu'elle a donné, ou son
 * téléphone quand elle a coché « le même numéro ». `null` si on ne peut pas la
 * joindre sur WhatsApp.
 */
export function whatsappDe(personne: {
  whatsapp?: string | null;
  whatsappEstLeTelephone?: boolean | null;
  telephone?: string | null;
}): string | null {
  if (personne.whatsappEstLeTelephone) return normaliserWhatsapp(personne.telephone);
  return normaliserWhatsapp(personne.whatsapp);
}

/** `+21622123456` → `22 123 456` pour un tunisien, `+33 6 12 34 56 78` sinon. */
export function whatsappLisible(numero: string): string {
  const national = /^\+216(\d{8})$/.exec(numero)?.[1];
  if (national) return `${national.slice(0, 2)} ${national.slice(2, 5)} ${national.slice(5)}`;
  return numero;
}

/**
 * `wa.me` veut le numéro **sans** `+` ni séparateur.
 * Rend `null` plutôt qu'un lien bancal : un bouton absent vaut mieux qu'un
 * bouton qui ouvre une conversation avec personne.
 */
export function lienWhatsapp(numero: string | null | undefined, message?: string): string | null {
  const normalise = normaliserWhatsapp(numero);
  if (!normalise) return null;
  const base = `https://wa.me/${normalise.slice(1)}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}

/**
 * Message pré-rempli du bouton « Postuler par WhatsApp ». La **référence** y
 * figure : c'est elle qui dira à l'équipe RH de quelle annonce il s'agit, la
 * réception restant manuelle dans ce lot.
 */
export function messageCandidatureWhatsapp(
  annonce: { intitule: string; reference: string | null },
  langue: 'fr' | 'ar',
): string {
  const reference = annonce.reference ?? '—';
  if (langue === 'ar') {
    return (
      `السلام عليكم، أرغب في الترشّح لمنصب « ${annonce.intitule} » (المرجع ${reference}). ` +
      'الاسم واللقب: … المدينة: … سأرسل سيرتي الذاتية إن وُجدت.'
    );
  }
  return (
    `Bonjour, je souhaite postuler au poste « ${annonce.intitule} » (réf. ${reference}). ` +
    'Nom et prénom : … Ville : … Je vous envoie mon CV si j’en ai un.'
  );
}
